// src/services/providerTest.js — provider catalogue + REAL connection testing.
//
// Fix #2 (docs/deploy_fixes.md): the "Test" button must be a real network
// round-trip, never a 450ms fake. Provider base URLs are resolved at save time
// and stored on the credential (custom providers keep their explicit endpoint);
// the test calls GET {base}/models with the stored key and reports the HTTP
// outcome + error body verbatim. Everything stays in the browser (the key is
// client-local by design).

import { PROVIDER_BASE_URL, NON_OPENAI_COMPAT, resolveProviderBaseUrl } from './providers.js';

// Provider picker options (also used by Bots model-source UI via ProviderConnections).
export const providerOptions = [
  { id: 'openrouter', name: 'OpenRouter', defaultModel: 'openai/gpt-4o-mini' },
  { id: 'google', name: 'Google', defaultModel: 'gemini-2.0-flash' },
  { id: 'zai', name: 'Z.ai', defaultModel: 'glm-4.5' },
  { id: 'deepseek', name: 'DeepSeek', defaultModel: 'deepseek-chat' },
  { id: 'anthropic', name: 'Anthropic', defaultModel: 'claude-3-5-sonnet' },
  { id: 'openai', name: 'OpenAI', defaultModel: 'gpt-4o-mini' },
  { id: 'custom', name: 'Custom', defaultModel: '' }
];

/**
 * Resolve the effective base URL for the form (save-time / hint display).
 * Known providers map through PROVIDER_BASE_URL; custom uses the typed
 * endpoint. Returns '' when unresolvable.
 */
export function resolveProviderBaseUrlForOption(providerId, customEndpoint = '') {
  if (providerId === 'custom') return (customEndpoint || '').trim().replace(/\/+$/, '');
  return PROVIDER_BASE_URL[providerId] || '';
}

/**
 * REAL connection test: GET {base}/models with the credential's key.
 * OK means the endpoint responded 2xx with a JSON model list. Any HTTP error
 * is reported with its status and a truncated body — a green "Test passed"
 * always means an authenticated round-trip succeeded.
 */
export async function testProviderConnection(connection) {
  const base = resolveProviderBaseUrl(connection);
  if (!base) {
    return {
      ok: false,
      message: `No endpoint for "${connection.provider}". Re-save with an OpenAI-compatible endpoint (or use Custom).`
    };
  }
  if (!connection.apiKey) {
    return { ok: false, message: 'No API key saved for this connection.' };
  }

  // Non-OpenAI-compatible providers can't be validated with GET /models — say
  // so instead of a wrong-format call or a fake pass.
  if (NON_OPENAI_COMPAT.includes(connection.provider)) {
    return {
      ok: false,
      message: `"${connection.provider}" is not OpenAI-compatible for direct browser calls. Re-save as Custom with an OpenAI-compatible endpoint.`
    };
  }

  let response;
  try {
    response = await fetch(`${base}/models`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${connection.apiKey}`,
        Accept: 'application/json'
      },
      signal: AbortSignal.timeout(15_000)
    });
  } catch (err) {
    return {
      ok: false,
      message: `Request failed: ${err?.name === 'AbortError' ? 'timed out after 15s' : (err?.message || 'network error')}`
    };
  }

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.text();
      detail = (body || '').slice(0, 240);
    } catch { /* ignore */ }
    return {
      ok: false,
      message: `HTTP ${response.status}${detail ? `: ${detail}` : ' — endpoint rejected the key or path.'}`
    };
  }

  try {
    const data = await response.json();
    const models = Array.isArray(data?.data) ? data.data : [];
    if (!models.length) {
      return {
        ok: true,
        message: `Connected (${(data?.data?.length ?? 'no')} models listed)`
      };
    }
    return {
      ok: true,
      message: `Connected — ${models.length} model${models.length === 1 ? '' : 's'} available (e.g. ${models.slice(0, 2).map((m) => m.id || m).join(', ')})`
    };
  } catch {
    return {
      ok: false,
      message: 'Endpoint answered 2xx but returned invalid JSON (not an OpenAI-compatible /models?).'
    };
  }
}
